// Admin: everyone the app knows, adding people without an account, invitation links and renaming

import React, { useMemo, useState } from 'react';
import { FiCopy, FiEdit2, FiLink, FiPlus } from 'react-icons/fi';
import { normalizeName, createPerson, createInvite, renamePerson } from './adminData';
import { recalculateUserPoints } from './recalculatePoints';

function AdminPeople({ user, roles, directory, reloadDirectory }) {
  const [form, setForm] = useState({ name: '', fullName: '', email: '' });
  const [inviteLinks, setInviteLinks] = useState({}); // personId -> link
  const [status, setStatus] = useState('');

  // One row per name: accounts first-class, people register for those without (or before) an account
  const rows = useMemo(() => {
    const byName = new Map();
    directory.users.forEach(u => byName.set(normalizeName(u.name), { name: u.name, account: true, userId: u.id }));
    directory.people.forEach(p => {
      const key = normalizeName(p.name);
      const existing = byName.get(key) || { name: p.name, account: !!p.uid };
      byName.set(key, { ...existing, person: p, account: existing.account || !!p.uid });
    });
    return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name, 'no'));
  }, [directory]);

  const exists = (name) => rows.some(r => normalizeName(r.name) === normalizeName(name));

  const addPerson = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return;
    if (exists(name)) return alert(`«${name}» finnes allerede. Bruk et kortnavn som skiller personene, f.eks. «Christian B».`);
    await createPerson(form, user.uid);
    setForm({ name: '', fullName: '', email: '' });
    await reloadDirectory();
  };

  const invite = async (person) => {
    const link = await createInvite(person, user.uid);
    setInviteLinks(l => ({ ...l, [person.id]: link }));
  };

  const copy = (link) => navigator.clipboard?.writeText(link);

  const rename = async (row) => {
    const newName = window.prompt(`Nytt navn for «${row.name}»:`, row.name.trim());
    if (!newName || newName.trim() === row.name) return;
    if (normalizeName(newName) !== normalizeName(row.name) && exists(newName)) {
      return alert(`«${newName.trim()}» finnes allerede.`);
    }
    try {
      setStatus(`Endrer navn på ${row.name}…`);
      const result = await renamePerson(row.name, newName);
      setStatus('Oppdaterer poeng…');
      await recalculateUserPoints();
      setStatus(`«${row.name}» heter nå «${newName.trim()}» (${result.events} eventer oppdatert).`);
      await reloadDirectory();
    } catch (error) {
      console.error('Error renaming: ', error);
      setStatus('Kunne ikke endre navn: ' + error.message);
    }
  };

  return (
    <div>
      <form className="admin-form" onSubmit={addPerson}>
        <h2>Ny person</h2>
        <p className="admin-hint">
          For personer som ikke har bruker ennå. De kan registreres som deltakere med en gang, og inviteres med en personlig lenke.
          Når de logger inn via lenken, kobles kontoen til navnet her.
        </p>
        <div className="admin-row">
          <div className="admin-field">
            <label>Kortnavn</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="F.eks. Ola" />
          </div>
          <div className="admin-field grow">
            <label>Fullt navn</label>
            <input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          </div>
          <div className="admin-field grow">
            <label>E-post (valgfritt)</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
        </div>
        <div className="admin-actions">
          <button type="submit" className="button-17"><FiPlus /> Legg til</button>
        </div>
      </form>

      {status && <p className="admin-status">{status}</p>}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Navn</th><th>Fullt navn</th><th>Konto</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.name} className="static">
                <td>
                  {/* Make stray whitespace visible, e.g. "Christian " */}
                  {row.name !== row.name.trim() ? <span className="admin-warn" title="Navnet har mellomrom på slutten">«{row.name}»</span> : row.name}
                </td>
                <td>{row.person?.fullName || ''}</td>
                <td>{row.account ? 'Ja' : 'Nei'}</td>
                <td className="admin-cell-actions">
                  {!row.account && row.person && (
                    inviteLinks[row.person.id] ? (
                      <span className="admin-invite">
                        <input readOnly value={inviteLinks[row.person.id]} onFocus={(e) => e.target.select()} />
                        <button className="admin-btn" onClick={() => copy(inviteLinks[row.person.id])} title="Kopier lenke"><FiCopy /></button>
                      </span>
                    ) : (
                      <button className="admin-btn" onClick={() => invite(row.person)}><FiLink /> Inviter</button>
                    )
                  )}
                  {roles.isSuperAdmin && (
                    <button className="admin-btn" onClick={() => rename(row)}><FiEdit2 /> Endre navn</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminPeople;
